import {PublishingOptionsList} from '@Panels/poster-publishing-options-panel/components/publishing-options-default-list/publishing-options-default-list.types';
import {uniqueArray} from '@Utils/array.util';
import {PublishingOptionsPerRow} from '@Panels/poster-publishing-options-panel/components/publishing-options-grid-section/publishing-options-grid-section.types';
import {DisplayType} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.types';
import type {PublishingOptionStats} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.types';
import type {DigitalSignageProviderSubFlow} from '@Panels/poster-publishing-options-panel/components/publishing-digital-signage-list/publishing-digital-signage-list.types';
import {ACTIVE_DIGITAL_SIGNAGE_PROVIDERS} from '@Panels/poster-publishing-options-panel/components/publishing-digital-signage-list/publishing-digital-signage-list-library';
import {SocialPlatform} from '@Components/social-media/social-media.types';

export type PublishingRecommendedEntry = PublishingOptionsList | DigitalSignageProviderSubFlow;

export const PUBLISH_DIALOG_SOCIAL_PLATFORMS: readonly SocialPlatform[] = [
  SocialPlatform.FACEBOOK,
  SocialPlatform.INSTAGRAM,
  SocialPlatform.TIKTOK,
  SocialPlatform.YOUTUBE,
  SocialPlatform.PINTEREST,
  SocialPlatform.LINKEDIN,
  SocialPlatform.GOOGLE_BUSINESS_PROFILE,
  SocialPlatform.TWITTER,
  SocialPlatform.THREADS,
];

export function isPublishingOption(value: string): value is PublishingOptionsList {
  const options: readonly string[] = Object.values(PublishingOptionsList);
  return options.includes(value);
}

export interface PublishingOptionDescriptor {
  iconName: string;
  labelKey: string;
  gaType: string;
}

export const PUBLISHING_SECTION_RECOMMENDED_TITLE_KEY = 'pmwjs_recommended';
export const PUBLISHING_SECTION_SOCIAL_TITLE_KEY = 'pmwjs_post_to_social';
export const PUBLISHING_SECTION_MORE_TITLE_KEY = 'pmwjs_more_ways_to_publish';

export const PUBLISHING_OPTION_DESCRIPTORS: Record<PublishingOptionsList, PublishingOptionDescriptor> = {
  [PublishingOptionsList.DOWNLOADED_POSTER]: {
    iconName: 'icon-cloud-download',
    labelKey: 'pmwjs_download',
    gaType: 'Download',
  },
  [PublishingOptionsList.SOCIAL_MEDIA]: {
    iconName: 'icon-social-media',
    labelKey: 'pmwjs_post_to_socials',
    gaType: 'SocialMediaPublishing',
  },
  [PublishingOptionsList.EMAIL_CAMPAIGN]: {
    iconName: 'icon-envelope',
    labelKey: 'pmwjs_send_email',
    gaType: 'EmailCampaign',
  },
  [PublishingOptionsList.WEB_LINK]: {
    iconName: 'icon-link',
    labelKey: 'pmwjs_publish_as_a_webpage',
    gaType: 'ShareHostedLink',
  },
  [PublishingOptionsList.SHARE_TO_APP]: {
    iconName: 'icon-share-external',
    labelKey: 'pmwjs_share_to_app',
    gaType: 'ShareToApp',
  },
  [PublishingOptionsList.PROMOTE_AS_EVENT]: {
    iconName: 'icon-event',
    labelKey: 'pmwjs_event_webpage',
    gaType: 'Events',
  },
  [PublishingOptionsList.QR_CODE]: {
    iconName: 'icon-qr-content',
    labelKey: 'pmwjs_qrcode',
    gaType: 'QRcode',
  },
  [PublishingOptionsList.DIGITAL_SIGNAGE]: {
    iconName: 'icon-tv',
    labelKey: 'pmwjs_publish_digital_signage',
    gaType: 'DigitalSignage',
  },
  [PublishingOptionsList.SHARE_ON_SOCIAL_MEDIA]: {
    iconName: 'icon-share-external',
    labelKey: 'pmwjs_link_to_social',
    gaType: 'SocialLink',
  },
  [PublishingOptionsList.EMBED_IN_WEBPAGE]: {
    iconName: 'icon-code',
    labelKey: 'pmwjs_embed_on_webpage',
    gaType: 'Embed',
  },
  [PublishingOptionsList.INVITE]: {
    iconName: 'icon-group-add',
    labelKey: 'pmwjs_share',
    gaType: 'Invite',
  },
};

const PUBLISHING_OPTION_CANONICAL_ORDER: readonly PublishingOptionsList[] = [
  PublishingOptionsList.DOWNLOADED_POSTER,
  PublishingOptionsList.SOCIAL_MEDIA,
  PublishingOptionsList.SHARE_TO_APP,
  PublishingOptionsList.EMAIL_CAMPAIGN,
  PublishingOptionsList.PROMOTE_AS_EVENT,
  PublishingOptionsList.WEB_LINK,
  PublishingOptionsList.QR_CODE,
  PublishingOptionsList.DIGITAL_SIGNAGE,
  PublishingOptionsList.SHARE_ON_SOCIAL_MEDIA,
  PublishingOptionsList.EMBED_IN_WEBPAGE,
  PublishingOptionsList.INVITE,
];

const RECOMMENDED_IDS_DESKTOP: readonly PublishingOptionsList[] = [
  PublishingOptionsList.DOWNLOADED_POSTER,
  PublishingOptionsList.SOCIAL_MEDIA,
  PublishingOptionsList.EMAIL_CAMPAIGN,
  PublishingOptionsList.WEB_LINK,
];

const RECOMMENDED_IDS_MOBILE: readonly PublishingOptionsList[] = [PublishingOptionsList.DOWNLOADED_POSTER, PublishingOptionsList.SOCIAL_MEDIA, PublishingOptionsList.SHARE_TO_APP];

const expandCanonicalEntryForTieBreak = (id: PublishingOptionsList): readonly PublishingRecommendedEntry[] => {
  return id === PublishingOptionsList.DIGITAL_SIGNAGE ? ACTIVE_DIGITAL_SIGNAGE_PROVIDERS : [id];
};

const USAGE_TIE_BREAK_ORDER: readonly PublishingRecommendedEntry[] = PUBLISHING_OPTION_CANONICAL_ORDER.flatMap(expandCanonicalEntryForTieBreak);

interface RankedUsageEntry {
  entry: PublishingRecommendedEntry;
  count: number;
}

/**
 * Every clicked option/provider by usage, most-clicked first — Download never competes (it is always the first Recommended tile) and providers rank individually.
 */
function getUsageRankedEntries(usageStats: PublishingOptionStats, excluded: ReadonlySet<PublishingOptionsList>): PublishingRecommendedEntry[] {
  const candidates: RankedUsageEntry[] = [];

  for (const option of Object.values(PublishingOptionsList)) {
    if (option === PublishingOptionsList.DOWNLOADED_POSTER || option === PublishingOptionsList.DIGITAL_SIGNAGE || excluded.has(option)) {
      continue;
    }
    const count = usageStats[option] ?? 0;
    if (count > 0) {
      candidates.push({entry: option, count});
    }
  }

  const providerCounts = usageStats[PublishingOptionsList.DIGITAL_SIGNAGE];
  if (providerCounts && !excluded.has(PublishingOptionsList.DIGITAL_SIGNAGE)) {
    for (const provider of ACTIVE_DIGITAL_SIGNAGE_PROVIDERS) {
      const count = providerCounts[provider] ?? 0;
      if (count > 0) {
        candidates.push({entry: provider, count});
      }
    }
  }

  candidates.sort((a, b) => {
    return b.count - a.count || USAGE_TIE_BREAK_ORDER.indexOf(a.entry) - USAGE_TIE_BREAK_ORDER.indexOf(b.entry);
  });

  return candidates.map((candidate) => {
    return candidate.entry;
  });
}

/**
 * Recommended is Download, then the user's most-clicked options/providers, then backfill (per-breakpoint defaults, then canonical order) to fill the row; deduped and truncated once, and that single truncation enforces top-N.
 */
function getUsageBasedRecommendedIds(
  usageStats: PublishingOptionStats | null,
  excluded: ReadonlySet<PublishingOptionsList>,
  isMobile: boolean,
  itemsPerRow: PublishingOptionsPerRow
): PublishingRecommendedEntry[] {
  const rankedEntries = usageStats ? getUsageRankedEntries(usageStats, excluded) : [];

  const defaultIds = isMobile ? RECOMMENDED_IDS_MOBILE : RECOMMENDED_IDS_DESKTOP;
  const backfillIds = [...defaultIds, ...PUBLISHING_OPTION_CANONICAL_ORDER].filter((id) => {
    return !excluded.has(id);
  });

  // uniqueArray keeps first occurrences, so Download/ranked entries hold position and backfill dupes drop out.
  return uniqueArray<PublishingRecommendedEntry>([PublishingOptionsList.DOWNLOADED_POSTER, ...rankedEntries, ...backfillIds]).slice(0, itemsPerRow);
}

export interface PublishingOptionSectionsOpts {
  isMobile: boolean;
  isDesktopModal?: boolean;
  recommendedOverrideIds?: readonly PublishingOptionsList[];
  excludedIds?: readonly PublishingOptionsList[];
  usageStats?: PublishingOptionStats | null;
}

export interface PublishingOptionSections {
  itemsPerRow: PublishingOptionsPerRow;
  recommendedIds: readonly PublishingRecommendedEntry[];
  moreWaysIds: readonly PublishingOptionsList[];
}

export function splitPublishingOptionsIntoSections({
  isMobile,
  isDesktopModal = false,
  recommendedOverrideIds,
  excludedIds,
  usageStats,
}: PublishingOptionSectionsOpts): PublishingOptionSections {
  const excluded = new Set<PublishingOptionsList>(excludedIds ?? []);
  if (!isMobile) {
    excluded.add(PublishingOptionsList.SHARE_TO_APP);
  }

  const itemsPerRow = getItemsPerRow(isMobile, isDesktopModal);
  const recommendedIds = recommendedOverrideIds?.length
    ? recommendedOverrideIds.filter((id) => {
        return !excluded.has(id);
      })
    : getUsageBasedRecommendedIds(usageStats ?? null, excluded, isMobile, itemsPerRow);

  const recommendedSet = new Set<PublishingRecommendedEntry>(recommendedIds);
  const moreWaysIds = PUBLISHING_OPTION_CANONICAL_ORDER.filter((id) => {
    return !recommendedSet.has(id) && !excluded.has(id);
  });

  return {
    itemsPerRow,
    recommendedIds,
    moreWaysIds,
  };
}

function getItemsPerRow(isMobile: boolean, isDesktopModal: boolean): PublishingOptionsPerRow {
  if (isMobile) {
    return PublishingOptionsPerRow.THREE;
  }
  return isDesktopModal ? PublishingOptionsPerRow.FIVE : PublishingOptionsPerRow.FOUR;
}

export interface PublishingGridPaddingOpts {
  isMobile: boolean;
  displayType: DisplayType;
}

export function getPublishingGridPaddingClasses({isMobile, displayType}: PublishingGridPaddingOpts): string {
  if (!isMobile && displayType === DisplayType.MODAL) {
    return 'spacing-p-t-6 spacing-p-b-6 spacing-p-l-8 spacing-p-r-8';
  }
  return 'spacing-p-4';
}
